/**
 * @fileoverview CaptchaField — canonical Cloudflare Turnstile wrapper.
 * @module @puffin-core/captcha-ui/CaptchaField
 *
 * Unified CAPTCHA component shared by all Colony tenant web apps. Wraps
 * `@marsidev/react-turnstile`'s <Turnstile> and provides:
 *
 *   - Three rendering branches:
 *     1. Site key configured → real Turnstile widget.
 *     2. Dev mode, no site key → yellow DEV-bypass badge + auto-verify
 *        with 'dev-bypass-token' so forms aren't blocked.
 *     3. Prod mode, no site key → error-state alert.
 *   - Imperative ref API: getToken(), isVerified(), reset(),
 *     simulateVerification(), simulateExpiration(), simulateError().
 *   - Callbacks: onVerify, onExpire, onError, onLoad.
 *   - A11y: role=group + aria-label on the wrapper in every branch, aria-busy, aria-live=polite;
 *     the unavailable branch's alert INSIDE the group is the one feedback component
 *     (SAVE-FEEDBACK-CANON-001 P3).
 *
 * @example
 *   import { CaptchaField } from '@puffin-core/captcha-ui';
 *
 *   function SignupForm() {
 *     const captchaRef = useRef();
 *     return (
 *       <form>
 *         <CaptchaField ref={captchaRef} onVerify={handleVerified} />
 *         <button onClick={() => submit(captchaRef.current.getToken())}>
 *           Sign up
 *         </button>
 *       </form>
 *     );
 *   }
 */

import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import { Turnstile } from '@marsidev/react-turnstile';
import { InlineFeedback } from '@puffin-core/feedback-ui';
import { TURNSTILE_SITE_KEY, SHOW_DEV_WARNING } from './captchaConfig.js';
import { useCaptchaTranslate } from './useCaptchaTranslate.js';

/**
 * The field's props.
 *
 * @typedef {object} CaptchaFieldProps
 * @property {string} [siteKey] Override the env-var site key (for testing).
 * @property {string} [className] Additional CSS classes on the wrapper.
 * @property {(token: string) => void} [onVerify] Called on successful verify.
 * @property {() => void} [onExpire] Called when the token expires.
 * @property {(err: Error|string) => void} [onError] Called on widget error.
 * @property {() => void} [onLoad] Called when the Turnstile widget loads.
 * @property {'light'|'dark'} [theme='light'] Widget theme.
 * @property {'normal'|'compact'|'flexible'} [size='normal'] Widget size.
 * @property {string} [testId='captcha-wrapper'] data-testid on the wrapper.
 * @property {(key: string) => string} [t] Optional translator override. The
 *   component already resolves the `captcha` namespace off the i18n instance in
 *   the React tree, so this is only for a caller that wants to supply its own
 *   copy — it is FIRST in the resolution order (AAS-I18N-002 W8 / ruling 25).
 * @property {string} [action] Turnstile's action: what the token is for (e.g. 'place_order'). The verifying
 *   server refuses a token whose action does not match its form. Omitted from the widget options when not given.
 */

/**
 * What the field's ref exposes.
 *
 * @typedef {object} CaptchaHandle
 * @property {() => ?string} getToken - the current token, or null before a verify (and after a reset)
 * @property {() => boolean} isVerified - whether the current token is verified
 * @property {() => void} reset - clears the token and issues a fresh challenge
 */

/**
 * CaptchaField — CAPTCHA verification component using Cloudflare Turnstile.
 *
 * The JSDoc sits on the function `forwardRef` receives: on the `const` it typed nothing, so every prop read as
 * missing from `{}`.
 */
const CaptchaField = forwardRef(
  /**
   * @param {CaptchaFieldProps} props
   * @param {React.Ref<CaptchaHandle>} ref Imperative ref to access verification state.
   * @returns {React.ReactElement}
   */
  function CaptchaField(
    {
      siteKey,
      className = '',
      onVerify,
      onExpire,
      onError,
      onLoad,
      theme = 'light',
      size = 'normal',
      testId = 'captcha-wrapper',
      t: consumerT,
      action,
    },
    ref,
  ) {
    // AAS-I18N-002 W8 / ruling 25: the three `aria-label`s and the one visible
    // error sentence below were hardcoded English in a SHARED PACKAGE rendered on
    // the first screen of all ten apps. No app-scoped census can see a literal in
    // `packages/**` (RECIPE miss-class 9).
    const translate = useCaptchaTranslate(consumerT);
    const tokenRef = useRef(null);
    const verifiedRef = useRef(false);
    const turnstileRef = useRef(null);

    // `isLoading` was unused — it was only ever initialised to false and
    // never flipped to true through any code path. Removed per Gen-0
    // dead-code policy. The `aria-busy` attribute below is fixed at
    // 'false' since the widget exposes no loading state to consumers.
    const [, forceUpdate] = useState({});

    const onVerifyRef = useRef(onVerify);
    const onExpireRef = useRef(onExpire);
    const onErrorRef = useRef(onError);
    const onLoadRef = useRef(onLoad);

    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
    onLoadRef.current = onLoad;

    const effectiveSiteKey = siteKey || TURNSTILE_SITE_KEY;

    const handleSuccess = (token) => {
      tokenRef.current = token;
      verifiedRef.current = true;
      forceUpdate({});
      if (onVerifyRef.current) {
        onVerifyRef.current(token);
      }
    };

    const handleExpire = () => {
      tokenRef.current = null;
      verifiedRef.current = false;
      forceUpdate({});
      if (onExpireRef.current) {
        onExpireRef.current();
      }
    };

    const handleError = (error) => {
      tokenRef.current = null;
      verifiedRef.current = false;
      forceUpdate({});
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    };

    const handleWidgetLoad = () => {
      if (onLoadRef.current) {
        onLoadRef.current();
      }
    };

    useEffect(() => {
      if (SHOW_DEV_WARNING) {
        handleSuccess('dev-bypass-token');
      }
    }, []);

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => tokenRef.current,
        isVerified: () => verifiedRef.current,
        reset: () => {
          tokenRef.current = null;
          verifiedRef.current = false;
          forceUpdate({});
          // reset() restores each render branch's MOUNT-STATE invariant. The
          // guard exists because <Turnstile> mounts in only ONE of the three
          // branches — `turnstileRef.current` is null in the other two, so an
          // unguarded call is a TypeError on every reset (e.g. login-shell's
          // resetCaptcha() from the login-failure catch):
          //   1. Real widget (site key): delegate so Turnstile issues a fresh
          //      challenge.
          //   2. Dev bypass (SHOW_DEV_WARNING): re-issue the auto-verify —
          //      bypass mode is always-verified; without this, one failed
          //      login would leave getToken() null forever.
          //   3. Prod without a key: nothing is mounted — state-clear only.
          if (turnstileRef.current) {
            turnstileRef.current.reset();
          } else if (SHOW_DEV_WARNING) {
            handleSuccess('dev-bypass-token');
          }
        },
        simulateVerification: (verificationToken) => {
          tokenRef.current = verificationToken;
          verifiedRef.current = true;
          forceUpdate({});
          if (onVerifyRef.current) {
            onVerifyRef.current(verificationToken);
          }
        },
        simulateExpiration: () => {
          tokenRef.current = null;
          verifiedRef.current = false;
          forceUpdate({});
          if (onExpireRef.current) {
            onExpireRef.current();
          }
        },
        simulateError: (error) => {
          tokenRef.current = null;
          verifiedRef.current = false;
          forceUpdate({});
          if (onErrorRef.current) {
            onErrorRef.current(error);
          }
        },
      }),
      [],
    );

    const wrapperClassName = [
      'form-group flex-col items-center gap-sm',
      verifiedRef.current ? 'border-success' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();

    if (effectiveSiteKey) {
      return (
        <div
          className={wrapperClassName}
          data-testid={testId}
          role="group"
          aria-label={translate('captcha.ariaLabel')}
          aria-busy="false"
          aria-live="polite"
        >
          <Turnstile
            ref={turnstileRef}
            siteKey={effectiveSiteKey}
            onSuccess={handleSuccess}
            onError={handleError}
            onExpire={handleExpire}
            onWidgetLoad={handleWidgetLoad}
            options={{
              theme,
              size: size === 'normal' ? 'normal' : size === 'compact' ? 'compact' : 'flexible',
              // The action names what the token is for, so the server can refuse a token minted by another
              // widget (LIFE-SHORTLISTED-001 S3). Forwarded only when given.
              ...(action ? { action } : {}),
            }}
          />
        </div>
      );
    }

    if (SHOW_DEV_WARNING) {
      return (
        <div
          className={wrapperClassName}
          data-testid={testId}
          role="group"
          aria-label={translate('captcha.ariaLabelDev')}
          aria-live="polite"
        >
          <div className="captcha-placeholder w-full p-sm">
            <div className="captcha-dev-warning flex-row items-center justify-center gap-sm">
              {/* Q-MI-25: the one tone pill, spelled raw — captcha-ui does not depend on
                page-shell, and a dev-only marker is not worth a dependency. */}
              <span className="badge" data-tone="warning">
                {translate('captcha.dev.badge')}
              </span>
              {/* PACKAGE-I18N-SEAT: translated like every other word the field says; the
                variable's NAME stays literal inside the sentence in every language. */}
              <span className="captcha-dev-text text-muted text-sm">
                {translate('captcha.dev.bypassed')}
              </span>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className={wrapperClassName}
        data-testid={testId}
        role="group"
        aria-label={translate('captcha.unavailable.ariaLabel')}
      >
        <InlineFeedback
          tone="error"
          message={translate('captcha.unavailable.message')}
          className="text-sm"
          testId={`${testId}-alert`}
        />
      </div>
    );
  },
);

CaptchaField.propTypes = {
  siteKey: PropTypes.string,
  className: PropTypes.string,
  onVerify: PropTypes.func,
  onExpire: PropTypes.func,
  onError: PropTypes.func,
  onLoad: PropTypes.func,
  theme: PropTypes.oneOf(['light', 'dark']),
  size: PropTypes.oneOf(['normal', 'compact', 'flexible']),
  testId: PropTypes.string,
  t: PropTypes.func,
  // Turnstile's `action`: the verifying server checks it matches the form (e.g. 'place_order').
  action: PropTypes.string,
};

export { CaptchaField };
export default CaptchaField;
